// src/components/Investment/InvestmentQuestionnaire.jsx - FIXED TOKEN + CONSTANTS
import React, { useState, useEffect } from 'react';
import { investmentService } from '../../services/investmentService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './InvestmentQuestionnaire.css';

const InvestmentQuestionnaire = ({ stock, onComplete, onClose, onError }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const { currency, formatCurrency, getCurrencySymbol } = useCurrency();

  const [formData, setFormData] = useState({
    investment_goal: '',
    time_horizon: '',
    risk_tolerance: '',
    emergency_fund: '',
    withdrawal_preference: '',
    fixed_withdrawal_amount: '',
    profit_withdrawal_percentage: '',
    intended_investment_amount: ''
  });

  useEffect(() => {
    fetchWalletBalance();
    fetchExchangeRate();
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/wallet/balance/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      console.log('📡 Wallet API Response Status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('💰 Wallet Balance Response:', data);
        console.log('💱 Currency in response:', data.currency);
        console.log('📊 Balance value:', data.balance);
        
        setPortfolioSummary({
          wallet_balance: data.balance,
          available_balance: data.available_balance,
          currency: data.currency
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch wallet balance:', response.status, errorText);
        setPortfolioSummary({ wallet_balance: 0, available_balance: 0 });
      }
    } catch (err) {
      console.error('❌ Error fetching wallet balance:', err);
      setPortfolioSummary({ wallet_balance: 0, available_balance: 0 });
    }
  };

  const fetchExchangeRate = async () => {
    try {
      
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        console.log('💱 Exchange Rate:', data);
        setExchangeRate(data);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  const convertFromNGN = (ngnAmount) => {
    if (!exchangeRate) {
      console.warn('⚠️ No exchange rate available for NGN to USD conversion');
      return ngnAmount;
    }
    
    const amount = parseFloat(ngnAmount);
    const rate = parseFloat(exchangeRate.ngn_to_usd);
    const converted = amount * rate;
    
    console.log('💱 NGN→USD Conversion:', {
      input: amount,
      rate: rate,
      output: converted
    });
    
    return converted;
  };

  const convertToNGN = (usdAmount) => {
    if (!exchangeRate) {
      console.warn('⚠️ No exchange rate available for USD to NGN conversion');
      return usdAmount;
    }
    
    const amount = parseFloat(usdAmount);
    const rate = parseFloat(exchangeRate.usd_to_ngn);
    const converted = amount * rate;
    
    console.log('💱 USD→NGN Conversion:', {
      inputUSD: amount,
      rate: rate,
      outputNGN: converted,
      expectedFor10k: amount === 10000 ? '₦14,504,100' : 'N/A'
    });
    
    return converted;
  };

  const getWalletBalance = () => {
    if (!portfolioSummary) {
      console.log('⚠️ No portfolioSummary available');
      return '0.00';
    }
    
    const balance = portfolioSummary.wallet_balance || 0;
    
    console.log('💰 Wallet balance (already converted by backend):', balance);
    console.log('💱 Currency:', portfolioSummary.currency || currency);
    
    return formatCurrency(balance);
  };

  const getWalletBalanceNumber = () => {
    if (!portfolioSummary) return 0;
    
    const balance = portfolioSummary.wallet_balance || 0;
    
    console.log('🔢 Numeric balance (already converted by backend):', balance);
    
    return balance;
  };

  // ✅ Must mirror services.py → InvestmentService.MINIMUM_INVESTMENT_AMOUNTS exactly
  const MINIMUM_INVESTMENT_BY_STAGE = {
    LISTED:         10000,
    FPO:            300000,
    PRE_IPO:        1000000,
    PRIVATE_EQUITY: 5000000,
  };

  const getMinimumInvestment = () => {
    const minNGN = MINIMUM_INVESTMENT_BY_STAGE[stock.listing_stage] || 10000;
    if (currency === 'NGN') return minNGN;
    return convertFromNGN(minNGN);
  };

  const getMinimumInvestmentDisplay = () => {
    return formatCurrency(getMinimumInvestment());
  };

  const getStockPrice = () => {
    if (stock.current_price_display) {
      return stock.current_price_display;
    }
    
    if (stock.formatted_price) {
      return stock.formatted_price;
    }
    
    if (stock.current_price) {
      return formatCurrency(stock.current_price);
    }
    
    return formatCurrency(0);
  };

  const shouldDisablePeriodicWithdrawals = () => {
    return formData.time_horizon === '4W';
  };

  const getPeriodicDisabledReason = () => {
    if (formData.time_horizon === '4W') {
      return '⚠️ Not recommended for 4-week investments. Your investment would mature before meaningful withdrawals can occur.';
    }
    return '';
  };

  const questions = {
    1: {
      title: 'Investment Goals',
      question: 'What is your primary investment goal?',
      field: 'investment_goal',
      options: [
        { value: 'PRESERVE', label: 'Preserve my capital' },
        { value: 'GROW_STEADY', label: 'Grow my money steadily over time' },
        { value: 'MAXIMIZE', label: 'Maximize long-term returns' }
      ]
    },
    2: {
      title: 'Investment Time Horizon',
      question: 'How long do you plan to invest before needing your money?',
      field: 'time_horizon',
      options: [
        { value: '4W', label: '4 weeks' },
        { value: '16W', label: '16 weeks' },
        { value: '32W', label: '32 weeks' },
        { value: '52W', label: '52 weeks' },
        { value: '1_5Y', label: '1-5 years' },
        { value: '5Y_PLUS', label: '5+ years' }
      ]
    },
    3: {
      title: 'Risk Tolerance',
      question: 'How do you feel if your investment drops 20% in a short time?',
      field: 'risk_tolerance',
      options: [
        { value: 'LOW', label: "Very worried — I'd want to sell" },
        { value: 'MEDIUM', label: "Concerned, but I'd wait and see" },
        { value: 'HIGH', label: "Unbothered — I'd see it as a buying opportunity" }
      ]
    },
    4: {
      title: 'Emergency Fund',
      question: 'Do you have an emergency fund (3–6 months of expenses)?',
      field: 'emergency_fund',
      options: [
        { value: 'NO', label: 'No' },
        { value: 'PARTIAL', label: 'Partially' },
        { value: 'YES', label: 'Yes' }
      ]
    },
    5: {
      title: 'Funds Management',
      question: 'How would you want to manage your funds?',
      field: 'withdrawal_preference',
      options: [
        { 
          value: 'PROFIT_PERIODIC', 
          label: 'I will like to withdraw profit accumulated over time, periodically (every 4 weeks)',
          disabled: shouldDisablePeriodicWithdrawals()
        },
        { 
          value: 'FIXED_PERIODIC', 
          label: 'I will like to withdraw a certain amount of money periodically, regardless of profit/loss (every 4 weeks)',
          disabled: shouldDisablePeriodicWithdrawals()
        },
        { 
          value: 'NO_WITHDRAWAL', 
          label: 'I have no plans to withdraw until estimated time' 
        }
      ]
    },
    6: {
      title: 'Investment Amount',
      question: `How much would you like to invest in ${stock.symbol}?`,
      field: 'intended_investment_amount',
      isInput: true
    }
  };

  const currentQuestion = questions[currentStep];

  const validateCurrentStep = () => {
    const field = currentQuestion.field;
    const value = formData[field];

    if (!value || value === '') {
      setError('Please select an option or enter a value');
      return false;
    }

    if (currentStep === 5 && formData.withdrawal_preference === 'FIXED_PERIODIC') {
      if (!formData.fixed_withdrawal_amount || parseFloat(formData.fixed_withdrawal_amount) <= 0) {
        setError('Please enter a valid withdrawal amount');
        return false;
      }
    }

    if (currentStep === 5 && formData.withdrawal_preference === 'PROFIT_PERIODIC') {
      const percentage = parseFloat(formData.profit_withdrawal_percentage);
      if (!formData.profit_withdrawal_percentage || percentage <= 0 || percentage > 100) {
        setError('Please enter a valid percentage between 1 and 100');
        return false;
      }
    }

    if (currentStep === 6) {
      const amount = parseFloat(formData.intended_investment_amount);
      const minInvestment = getMinimumInvestment();
      const walletBalance = getWalletBalanceNumber();
      
      console.log('💰 Validation Check:', {
        amount: amount,
        minInvestment: minInvestment,
        walletBalance: walletBalance,
        currency: currency
      });

      if (!amount || amount <= 0) {
        setError('Please enter a valid investment amount');
        return false;
      }

      if (amount < minInvestment) {
        setError(`Minimum investment is ${getMinimumInvestmentDisplay()}`);
        return false;
      }

      if (amount > walletBalance) {
        setError(`Insufficient balance. Available: ${getWalletBalance()}`);
        return false;
      }
    }

    return true;
  };

  const handleOptionSelect = (value) => {
    setFormData(prev => ({
      ...prev,
      [currentQuestion.field]: value
    }));
    setError(null);

    if (currentQuestion.field === 'time_horizon') {
      if (value === '4W' && (formData.withdrawal_preference === 'PROFIT_PERIODIC' || formData.withdrawal_preference === 'FIXED_PERIODIC')) {
        setFormData(prev => ({
          ...prev,
          withdrawal_preference: ''
        }));
      }
    }
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      return;
    }

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
      setError(null);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const amountInUserCurrency = parseFloat(formData.intended_investment_amount);
      
      console.log('🔐 Submitting (backend will convert):', {
        userInput: amountInUserCurrency,
        currency: currency,
        exchangeRate: exchangeRate
      });
      
      const questionnaireData = {
        stock_id: stock.id,
        investment_goal: formData.investment_goal,
        time_horizon: formData.time_horizon,
        risk_tolerance: formData.risk_tolerance,
        emergency_fund: formData.emergency_fund,
        withdrawal_preference: formData.withdrawal_preference,
        intended_investment_amount: amountInUserCurrency,
        input_currency: currency
      };

      if (formData.withdrawal_preference === 'FIXED_PERIODIC') {
        questionnaireData.fixed_withdrawal_amount = parseFloat(formData.fixed_withdrawal_amount);
      }

      if (formData.withdrawal_preference === 'PROFIT_PERIODIC') {
        questionnaireData.profit_withdrawal_percentage = parseFloat(formData.profit_withdrawal_percentage);
      }

      console.log('📤 Sending to backend:', questionnaireData);

      const response = await investmentService.submitQuestionnaire(questionnaireData);
      onComplete(response.questionnaire);
    } catch (err) {
      console.error('❌ Questionnaire submission error:', err);
      
      let errorData = null;
      
      try {
        if (err.response && err.response.data) {
          errorData = err.response.data;
        } 
        else if (err.message && err.message.includes('{')) {
          const jsonMatch = err.message.match(/\{.*\}/);
          if (jsonMatch) {
            errorData = JSON.parse(jsonMatch[0]);
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }

      if (errorData && errorData.existing_investment) {
        console.log('🔍 Found existing investment:', errorData.existing_investment);
        
        if (onError) {
          onError({
            existing_investment: errorData.existing_investment,
            error: errorData.error
          });
          return;
        }
      }
      
      const errorMessage = errorData?.error || err.message || 'Failed to submit questionnaire';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isLastStep = currentStep === 6;
  const showFixedAmountInput = currentStep === 5 && formData.withdrawal_preference === 'FIXED_PERIODIC';
  const showProfitPercentageInput = currentStep === 5 && formData.withdrawal_preference === 'PROFIT_PERIODIC';
  const showInvestmentAmountInput = currentStep === 6;

  return (
    <div className="investment-modal-overlay" onClick={onClose}>
      <div className="investment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="investment-modal-header">
          <div>
            <h2>Investment Questionnaire</h2>
            <p className="stock-name">{stock.symbol} - {stock.name}</p>
            <p className="stock-price">Current Price: {getStockPrice()}</p>
            <span className="currency-badge">{currency}</span>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {portfolioSummary && (
          <div className="wallet-balance-display">
            <span className="balance-label">Available Balance:</span>
            <span className="balance-amount">{getWalletBalance()}</span>
          </div>
        )}

        {currency === 'USD' && exchangeRate && (
          <div className="exchange-rate-badge" style={{padding: '8px', background: '#f0f0f0', borderRadius: '4px', margin: '10px 0', fontSize: '12px'}}>
            💱 1 USD = ₦{parseFloat(exchangeRate.usd_to_ngn).toFixed(2)}
          </div>
        )}

        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(currentStep / 6) * 100}%` }} />
          </div>
          <div className="questionnaire-progress-text">Step {currentStep} of 6</div>
        </div>

        <div className="question-content">
          <h3 className="question-title">{currentQuestion.title}</h3>
          <p className="question-text">{currentQuestion.question}</p>

          {currentStep === 5 && shouldDisablePeriodicWithdrawals() && (
            <div className="withdrawal-warning" style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#856404'
            }}>
              {getPeriodicDisabledReason()}
            </div>
          )}

          {!currentQuestion.isInput && (
            <div className="options-container">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={option.value}
                  className={`option-button ${formData[currentQuestion.field] === option.value ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
                  onClick={() => !option.disabled && handleOptionSelect(option.value)}
                  disabled={loading || option.disabled}
                  style={option.disabled ? {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    backgroundColor: '#f5f5f5',
                    color: '#999'
                  } : {}}
                >
                  <span className="option-letter">{String.fromCharCode(97 + index)})</span>
                  <span className="option-text">{option.label}</span>
                  {option.disabled && (
                    <span style={{
                      display: 'block',
                      fontSize: '11px',
                      marginTop: '4px',
                      fontStyle: 'italic',
                      color: '#666'
                    }}>
                      (Not available for 5-minute investments)
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {showFixedAmountInput && (
            <div className="input-container">
              <label>Fixed Withdrawal Amount ({getCurrencySymbol()})</label>
              <input
                type="number"
                step="0.01"
                min="1"
                value={formData.fixed_withdrawal_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, fixed_withdrawal_amount: e.target.value }))}
                placeholder={`Enter amount in ${currency}`}
                disabled={loading}
              />
            </div>
          )}

          {showProfitPercentageInput && (
            <div className="input-container">
              <label>Profit Withdrawal Percentage (%)</label>
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={formData.profit_withdrawal_percentage}
                onChange={(e) => setFormData(prev => ({ ...prev, profit_withdrawal_percentage: e.target.value }))}
                placeholder="e.g., 50 for 50%"
                disabled={loading}
              />
            </div>
          )}

          {showInvestmentAmountInput && (
            <div className="input-container investment-amount-input">
              <label>Investment Amount ({getCurrencySymbol()})</label>
              <input
                type="number"
                step="0.01"
                min={getMinimumInvestment()}
                value={formData.intended_investment_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, intended_investment_amount: e.target.value }))}
                placeholder={`Enter amount in ${currency}`}
                disabled={loading}
              />
              <span className="helper-text">
                Available: {getWalletBalance()}
              </span>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="navigation-buttons">
          <button
            className="nav-button secondary"
            onClick={handlePrevious}
            disabled={currentStep === 1 || loading}
          >
            Previous
          </button>
          <button
            className="nav-button primary"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? 'Submitting...' : (isLastStep ? 'Submit' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentQuestionnaire;